import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "./Users.css";

const API_URL = process.env.REACT_APP_API_URL;

const Users = () => {
  const [activeTab, setActiveTab] = useState("admins"); // 'admins' or 'customers'
  const [admins, setAdmins] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ name: "", email: "", password: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(null);

  const token = localStorage.getItem("token");

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/admins`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdmins(res.data.users);
    } catch (err) {
      toast.error("Failed to load admin users");
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(res.data.users);
    } catch (err) {
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "admins") fetchAdmins();
    else fetchCustomers();
  }, [activeTab]);

  // Block/Unblock (only for customers)
  const toggleStatus = async (id) => {
    try {
      setActionInProgress({ type: 'status', id });
      setLoading(true);
      await axios.put(`${API_URL}/admin/users/status/${id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User status updated");
      await fetchCustomers(); // Refresh only customers list
    } catch (err) {
      toast.error("Status update failed");
    } finally {
      setActionInProgress(null);
      setLoading(false);
    }
  };

  // Toggle role – only allowed for customers (promote to admin)
  const toggleRole = async (id) => {
    try {
      setActionInProgress({ type: 'role', id });
      setLoading(true);
      await axios.put(`${API_URL}/admin/users/role/${id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Role updated");
      // Refresh both lists because a user may change group
      await fetchAdmins();
      await fetchCustomers();
    } catch (err) {
      toast.error("Role update failed");
    } finally {
      setActionInProgress(null);
      setLoading(false);
    }
  };

  // Delete admin
  const deleteAdmin = async (id) => {
    if (!window.confirm("Are you sure you want to delete this admin?")) return;
    try {
      setLoading(true);
      await axios.delete(`${API_URL}/admin/admins/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Admin deleted");
      await fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.message || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const res = await axios.post(`${API_URL}/admin/users/create-admin`, newAdmin, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        toast.success("Admin added successfully");
        setShowAddAdmin(false);
        setNewAdmin({ name: "", email: "", password: "", phone: "" });
        await fetchAdmins(); // Refresh admin list
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to add admin");
    } finally {
      setLoading(false);
    }
  };

  const filteredList = (activeTab === "admins" ? admins : customers).filter(user => {
    const name = (user.name || '').toLowerCase();
    const email = (user.email || '').toLowerCase();
    const searchTerm = search.toLowerCase();
    return name.includes(searchTerm) || email.includes(searchTerm);
  });

  return (
    <div className="users-container">
      {loading && (
        <div className="dash-loader-overlay">
          <div className="dash-loader-container">
            <div className="dash-spinner"></div>
          </div>
        </div>
      )}

      <div className="users-header">
        <h4>User Management</h4>
        <div className="header-actions">
          <div className="search-box">
            <i className="bi bi-search"></i>
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {activeTab === "admins" && (
            <button className="add-admin-btn" onClick={() => setShowAddAdmin(true)} disabled={loading}>
              <i className="bi bi-person-plus"></i> Add Admin
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="users-tabs">
        <button
          className={`tab-btn ${activeTab === "admins" ? "active" : ""}`}
          onClick={() => setActiveTab("admins")}
        >
          <i className="bi bi-shield-lock"></i> Admin Users
        </button>
        <button
          className={`tab-btn ${activeTab === "customers" ? "active" : ""}`}
          onClick={() => setActiveTab("customers")}
        >
          <i className="bi bi-people"></i> Website Users (Customers)
        </button>
      </div>

      {/* Table */}
      <div className="users-table">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              {activeTab === "customers" && (
                <>
                  <th>Total Orders</th>
                  <th>Total Purchase</th>
                </>
              )}
              <th>Role</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredList.length === 0 ? (
              <tr className="users-empty-row">
                <td colSpan={activeTab === "customers" ? 8 : 6}>
                  <div className="users-empty-state">
                    <i className="bi bi-people"></i>
                    <p>No users found</p>
                  </div>
                </td>
              </tr>
            ) : (
              filteredList.map(user => (
                <tr key={user.id}>
                  <td>{user.name}</td>
                  <td>{user.email}</td>
                  <td>{user.phone || "-"}</td>
                  {activeTab === "customers" && (
                    <>
                      <td>{user.total_orders || 0}</td>
                      <td>₹{(user.total_purchase || 0).toLocaleString()}</td>
                    </>
                  )}
                  <td>
                    <span className={`role-badge ${user.role === "admin" ? "admin" : "user"}`}>
                      {user.role}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${user.status === "Active" ? "active" : "blocked"}`}>
                      {user.status}
                    </span>
                  </td>
                  <td className="actions">
                    <button
                      className="view-btn"
                      onClick={() => setSelectedUser(user)}
                      title="View Details"
                    >
                      <i className="bi bi-eye"></i>
                    </button>

                    {activeTab === "admins" ? (
                      <button
                        className="delete-btn"
                        onClick={() => deleteAdmin(user.id)}
                        disabled={loading}
                        title="Delete Admin"
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    ) : (
                      <>
                        <button
                          className="role-btn"
                          onClick={() => toggleRole(user.id)}
                          disabled={actionInProgress?.type === 'role' && actionInProgress?.id === user.id}
                          title="Promote to Admin"
                        >
                          {actionInProgress?.type === 'role' && actionInProgress?.id === user.id ? (
                            <div className="btn-inline-spinner"></div>
                          ) : (
                            <i className="bi bi-shield-plus"></i>
                          )}
                        </button>

                        <button
                          className={user.status === "Active" ? "block-btn" : "unblock-btn"}
                          onClick={() => toggleStatus(user.id)}
                          disabled={actionInProgress?.type === 'status' && actionInProgress?.id === user.id}
                          title={user.status === "Active" ? "Block User" : "Unblock User"}
                        >
                          {actionInProgress?.type === 'status' && actionInProgress?.id === user.id ? (
                            <div className="btn-inline-spinner"></div>
                          ) : (
                            <i className={`bi ${user.status === "Active" ? "bi-person-x" : "bi-person-check"}`}></i>
                          )}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* User Details Modal - now shows orders/purchase for customers */}
      {selectedUser && (
        <div className="user-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h5>User Details</h5>
              <button onClick={() => setSelectedUser(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="user-detail-item">
                <span className="detail-label">Name:</span>
                <span>{selectedUser.name}</span>
              </div>
              <div className="user-detail-item">
                <span className="detail-label">Email:</span>
                <span>{selectedUser.email}</span>
              </div>
              <div className="user-detail-item">
                <span className="detail-label">Phone:</span>
                <span>{selectedUser.phone || "-"}</span>
              </div>
              {activeTab === "customers" && (
                <>
                  <div className="user-detail-item">
                    <span className="detail-label">Total Orders:</span>
                    <span>{selectedUser.total_orders || 0}</span>
                  </div>
                  <div className="user-detail-item">
                    <span className="detail-label">Total Purchase:</span>
                    <span>₹{(selectedUser.total_purchase || 0).toLocaleString()}</span>
                  </div>
                </>
              )}
              <div className="user-detail-item">
                <span className="detail-label">Role:</span>
                <span>{selectedUser.role}</span>
              </div>
              <div className="user-detail-item">
                <span className="detail-label">Status:</span>
                <span>{selectedUser.status}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Admin Modal */}
      {showAddAdmin && (
        <div className="user-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h5>Add New Admin</h5>
              <button onClick={() => setShowAddAdmin(false)}>✕</button>
            </div>
            <form onSubmit={handleAddAdmin} className="modal-body">
              <div className="form-group mb-3">
                <label>Full Name <span className="required-star">*</span></label>
                <input type="text" className="form-control" required value={newAdmin.name} onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })} disabled={loading} />
              </div>
              <div className="form-group mb-3">
                <label>Email <span className="required-star">*</span></label>
                <input type="email" className="form-control" required value={newAdmin.email} onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })} disabled={loading} />
              </div>
              <div className="form-group mb-3">
                <label>Password <span className="required-star">*</span></label>
                <div className="password-input-wrapper">
                  <input type={showPassword ? "text" : "password"} className="form-control" required value={newAdmin.password} onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })} disabled={loading} />
                  <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`} onClick={() => setShowPassword(!showPassword)}></i>
                </div>
              </div>
              <div className="form-group mb-3">
                <label>Phone (Optional)</label>
                <input type="text" className="form-control" value={newAdmin.phone} onChange={(e) => setNewAdmin({ ...newAdmin, phone: e.target.value })} disabled={loading} />
              </div>
              <button type="submit" className="btn-submit w-100" disabled={loading}>
                {loading ? <><div className="btn-spinner"></div> Creating...</> : "Create Admin"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;